import { Injectable } from '@angular/core';
import { CronPart, Frequency, Meridian, FrequencyCronPatterns, FrequencyOptions } from './cron.models';
import { FormGroup } from '@angular/forms';
import cronstrue from 'cronstrue';

@Injectable({
    providedIn: 'root'
})
export class CronService {
    readonly dayOfWeekOptions = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    dayOfMonthOptions = new Array<{ label: string; value: string; selectedLabel?: string }>();
    CRON_INVALID_MESSAGE = 'Invalid schedule';
    constructor() {
        this.setDaysOfMonth();
    }

    /** Given data from a plain-english cron form, generate a cron expression */
    public getCronExpression(data: any): string {
        let expression = '';

        const to24HrTime = data.meridian === Meridian.pm ? 12 + (data.hour % 12) : data.hour % 12;
        const daysOfMonth = data.daysOfMonth?.join(',');
        const daysOfWeek = data.daysOfWeek?.map((day: string) => day.slice(0, 3)).join(',');

        switch (data.frequencyId) {
            //cron: min hour day month dayweek
            case Frequency.min:
                expression = `*/${data.frequency} * * * *`;
                break;
            case Frequency.hour:
                expression = `${data.minute} */${data.frequency} * * *`;
                break;
            case Frequency.day:
                expression = `${data.minute} ${to24HrTime} */${data.frequency} * *`;
                break;
            case Frequency.week:
                expression = `${data.minute} ${to24HrTime} * * ${daysOfWeek}`;
                break;
            case Frequency.month:
                expression = `${data.minute} ${to24HrTime} ${daysOfMonth} */${data.frequency} *`;
                break;
        }
        return expression;
    }

    /** Given a cron expression, set up the plain-english cron form */
    public setFormForCron(form: FormGroup, cron: string): void {
        if (!cron) {
            return;
        }

        const cronParts = this.parseCronExpressionIntoParts(cron);
        const controls = form.controls;
        if (this.cronHasPatternMatch(cronParts, FrequencyCronPatterns[Frequency.min])) {
            controls['frequencyId'].setValue(Frequency.min);
            controls['frequency'].setValue(this.parseFrequency(cronParts[0]));
        } else if (this.cronHasPatternMatch(cronParts, FrequencyCronPatterns[Frequency.hour])) {
            controls['frequencyId'].setValue(Frequency.hour);
            controls['frequency'].setValue(this.parseFrequency(cronParts[1]));
            controls['minute'].setValue(cronParts[0].value);
        } else if (this.cronHasPatternMatch(cronParts, FrequencyCronPatterns[Frequency.day])) {
            controls['frequencyId'].setValue(Frequency.day);
            controls['frequency'].setValue(this.parseFrequency(cronParts[2]));
            this.convertTo24HourTime(form, cronParts);
        } else if (this.cronHasPatternMatch(cronParts, FrequencyCronPatterns[Frequency.week])) {
            controls['frequencyId'].setValue(Frequency.week);
            controls['daysOfWeek'].setValue(
                cronParts[4].value?.split(',').map(digit => this.dayOfWeekOptions[parseInt(digit)])
            );
            this.convertTo24HourTime(form, cronParts);
        } else if (this.cronHasPatternMatch(cronParts, FrequencyCronPatterns[Frequency.month])) {
            controls['frequencyId'].setValue(Frequency.month);
            controls['frequency'].setValue(this.parseFrequency(cronParts[3]));
            controls['daysOfMonth'].setValue(cronParts[2].value?.split(','));
            this.convertTo24HourTime(form, cronParts);
        } else {
            // Intentionally treat unmatched cron patterns as custom in the editor UI.
            // This includes 6-field expressions and other valid patterns that do not
            // map to one of our predefined frequency templates.
            controls['frequencyId'].setValue(Frequency.custom);
            controls['rawCron'].setValue(cron);
        }
    }

    public getFrequencyBitMask(cron: string): number {
        const frequencyOption = FrequencyOptions.filter(o => o.id !== Frequency.custom).find(o =>
            this.cronPatternMatchesFrequency(cron, o.id)
        );
        return frequencyOption?.mask ?? 0;
    }

    /** Translate a raw cron string into human readable description of the schedule. */
    public translateCron(rawCron?: string | null): string {
        let englishDesc = this.CRON_INVALID_MESSAGE;
        try {
            if (rawCron) {
                const parts = rawCron.trim().split(/\s+/);
                if (parts.length === 5 || parts.length === 6) {
                    englishDesc = cronstrue.toString(rawCron, { verbose: true });
                }
            }
        } catch {
            /* empty */
        }

        return englishDesc;
    }

    public translateCronWithUtc(rawCron?: string | null): string {
        const translated = this.translateCron(rawCron);
        return translated === this.CRON_INVALID_MESSAGE ? translated : `${translated} (UTC)`;
    }

    private cronPatternMatchesFrequency(cron: string, frequency: Frequency): boolean {
        if (!cron) {
            return false;
        }

        const cronParts = this.parseCronExpressionIntoParts(cron);
        return this.cronHasPatternMatch(cronParts, FrequencyCronPatterns[frequency]);
    }

    private parseCronExpressionIntoParts(cron: string): Array<CronPart> {
        const skipCronRegex = /^\*\/\d+$/;
        const digitRegex = /^\d+$/;
        const lastRegex = /^L$/;
        const listRegex = /^(\d+|L)[,-]((\d+|L)[,-]?)+$/;
        return cron
            .trim()
            .split(/\s+/)
            .map((part, index) => {
                if (index === 4) {
                    part = this.convertDaysOfWeekToNumberAsNeeded(part);
                }
                return {
                    isSkip: skipCronRegex.test(part),
                    isNumber: digitRegex.test(part),
                    isList: listRegex.test(part),
                    isLast: lastRegex.test(part),
                    isStar: part === '*',
                    value: part
                };
            });
    }

    // This method is needed to convert named days of week in cron expressions into numeric form
    // For example "Mon-Wed" needs to be converted to "1,2,3" and "Mon,Wed-Fri" needs to be converted to
    // "1,3,5". Cron expressions with day of week number ranges like "1-5" are also converted to "1,2,3,4,5".
    private convertDaysOfWeekToNumberAsNeeded(cronPartValue: string): string {
        const findNamedIndex = (val: string) =>
            this.dayOfWeekOptions.findIndex(day => day.toString().slice(0, 3).toLowerCase() === val.toLowerCase());

        return cronPartValue
            .split(',')
            .map(chunk => {
                const rangeChunks = chunk.split('-');
                if (rangeChunks.length > 1) {
                    const startNamedIndex = findNamedIndex(rangeChunks[0]);
                    const endNamedIndex = findNamedIndex(rangeChunks[1]);
                    const start = startNamedIndex > -1 ? startNamedIndex : parseInt(rangeChunks[0]);
                    const end = endNamedIndex > -1 ? endNamedIndex : parseInt(rangeChunks[1]);
                    return Array.from({ length: end - start + 1 }, (_, i) => (i + start).toString()).join(',');
                }
                const idx = findNamedIndex(chunk);
                return idx > -1 ? idx.toString() : chunk;
            })
            .join(',');
    }

    private parseFrequency(cronPart: CronPart): string {
        if (cronPart.isSkip) {
            return cronPart.value?.slice(2, cronPart.value.length) || '0';
        } else {
            return '1';
        }
    }

    private convertTo24HourTime(form: FormGroup, cronParts: Array<CronPart>): void {
        const rawHour = parseInt(cronParts[1].value || '0');
        const hour = rawHour % 12;
        const minute = parseInt(cronParts[0].value || '0');
        form.controls['minute'].setValue(minute.toString());
        form.controls['hour'].setValue(hour.toString());
        form.controls['meridian'].setValue(rawHour > 12 ? Meridian.pm : Meridian.am);
    }

    private cronHasPatternMatch(cronPattern1: Array<CronPart>, cronPatternsToMatch: Array<Array<CronPart>>): boolean {
        return cronPatternsToMatch.some(patternToMatch => {
            return cronPattern1.every((part, index) => {
                const partToMatch = patternToMatch[index];
                return (
                    part.isNumber == !!partToMatch?.isNumber &&
                    part.isSkip == !!partToMatch?.isSkip &&
                    part.isStar == !!partToMatch?.isStar
                );
            });
        });
    }

    private setDaysOfMonth() {
        this.dayOfMonthOptions = [...Array(31).keys()].map(x => {
            const day = (x + 1).toString();
            return { value: day, label: this.getOrdinal(day) };
        });
        this.dayOfMonthOptions.push({ value: 'L', label: 'Last day of month', selectedLabel: 'Last day' });
    }

    private getOrdinal(n: string) {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = parseInt(n) % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }
}
